import React, { useEffect, useMemo, useState } from 'react'
import { Tab, Transition } from '@headlessui/react'
import type { Property } from 'csstype'

import {
  digitalCertificateBenefits,
  digitalCertificateDescription,
  meiDescription,
  lucroPresumidoDescription,
  lucroRealDescription,
  planBenefits,
  simplesNacionalDescription,
} from 'components/Plans/constants'

import { classNames } from 'services/string'
import { findContrastColor } from 'services/color'

import PlanCard from 'components/PlanCard'

export enum PlanCategory {
  Service = 'Serviço',
  Commerce = 'Comércio',
}

interface PlanContent extends Queries.PlansContentFragment {
  category?: PlanCategory
  name: string
  description: string
  metaDescription: string
}

export interface CustomPricing extends Queries.PlansContentFragment {
  name: string
  description: string
  metaDescription: string
  category: PlanCategory
}

interface PlansProps {
  primaryColor?: Property.Color
  secondaryColor?: Property.Color
  pricing?: Queries.PricingInfoFragment['pricing']
  customPricingOptions?: CustomPricing[]
  planBenefitsTitle?: string
  customPlanBenefits?: string[]
  redirectToTarget?: boolean
  benefitsCardOptions?: {
    wrapped?: boolean
  }
  planCardOptions?: {
    blurred?: boolean
  }
  offerPlan?: boolean
  offerDiscount?: number
}

const Plans = ({
  pricing,
  customPricingOptions,
  planBenefitsTitle,
  customPlanBenefits,
  primaryColor,
  secondaryColor,
  benefitsCardOptions,
  planCardOptions,
  redirectToTarget,
  offerPlan,
  offerDiscount,
}: PlansProps) => {
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0)
  const [availableOptions, setAvailableOptions] = useState<PlanContent[]>()
  const [digitalCertificateOption, setDigitalCertificateOption] = useState<PlanContent>()
  const [shouldShowTabOptions, setShouldShowTabOptions] = useState<boolean>(false)

  const primaryContrastColor = findContrastColor(primaryColor || 'var(--color-primary-600)')

  useEffect(() => {
    if (customPricingOptions) {
      setAvailableOptions(customPricingOptions)
      return
    }

    const {
      digitalCertificate,
      meiService,
      meiCommerce,
      simplesNacionalService,
      simplesNacionalCommerce,
      lucroPresumidoService,
      lucroPresumidoCommerce,
      lucroRealService,
      lucroRealCommerce,
    } = pricing || {}

    const options: PlanContent[] = [
      ...(meiService
        ? [
            {
              category: PlanCategory.Service,
              metaDescription: 'meiService',
              ...meiDescription,
              ...meiService,
            },
          ]
        : []),
      ...(simplesNacionalService
        ? [
            {
              category: PlanCategory.Service,
              metaDescription: 'simplesNacionalService',
              ...simplesNacionalDescription,
              ...simplesNacionalService,
            },
          ]
        : []),
      ...(meiCommerce
        ? [
            {
              category: PlanCategory.Commerce,
              metaDescription: 'meiCommerce',
              ...meiDescription,
              ...meiCommerce,
            },
          ]
        : []),
      ...(simplesNacionalCommerce
        ? [
            {
              category: PlanCategory.Commerce,
              metaDescription: 'simplesNacionalCommerce',
              ...simplesNacionalDescription,
              ...simplesNacionalCommerce,
            },
          ]
        : []),
      ...(lucroPresumidoCommerce
        ? [
            {
              category: PlanCategory.Commerce,
              metaDescription: 'lucroPresumidoCommerce',
              ...lucroPresumidoDescription,
              ...lucroPresumidoCommerce,
            },
          ]
        : []),
      ...(lucroPresumidoService
        ? [
            {
              category: PlanCategory.Service,
              metaDescription: 'lucroPresumidoService',
              ...lucroPresumidoDescription,
              ...lucroPresumidoService,
            },
          ]
        : []),
      ...(lucroRealCommerce
        ? [
            {
              category: PlanCategory.Commerce,
              metaDescription: 'lucroRealCommerce',
              ...lucroRealDescription,
              ...lucroRealCommerce,
            },
          ]
        : []),
      ...(lucroRealService
        ? [
            {
              category: PlanCategory.Service,
              metaDescription: 'lucroRealService',
              ...lucroRealDescription,
              ...lucroRealService,
            },
          ]
        : []),
    ]
    setAvailableOptions(options)
    setDigitalCertificateOption(
      digitalCertificate
        ? {
            metaDescription: 'digitalCertificate',
            ...digitalCertificateDescription,
            ...digitalCertificate,
          }
        : undefined,
    )

    const availableCategoriesToShow =
      options &&
      options.reduce(
        (prev, { category }) => (category ? (prev.includes(category) ? prev : [...prev, category]) : prev),
        [] as PlanCategory[],
      )
    setShouldShowTabOptions(Boolean(availableCategoriesToShow.length > 1))
    setSelectedTabIndex(Object.values(PlanCategory).indexOf(availableCategoriesToShow[0]))
  }, [pricing])

  const optionsToShow = useMemo(() => {
    const selectedTab = Object.values(PlanCategory)[selectedTabIndex]
    if (!selectedTab) return availableOptions
    return availableOptions?.filter(({ category }) => category === String(selectedTab))
  }, [availableOptions, selectedTabIndex])

  return (
    <div className='flex flex-col gap-8 sm:gap-12 lg:gap-16'>
      <div className={classNames('justify-center w-full', shouldShowTabOptions ? 'flex' : 'hidden')}>
        <Tab.Group onChange={(index: number) => setSelectedTabIndex(index)}>
          <Tab.List className='flex space-x-1 rounded-xl p-1 bg-white border-gray-400 border drop-shadow-lg' id='price-tab-group'>
            {Object.values(PlanCategory).map((category, index) => (
              <Tab
                id={`price-tab-${category}`}
                value={category}
                key={category}
                className={({ selected }) =>
                  classNames(
                    'rounded-lg py-2.5 px-1 text-xs font-normal leading-5 w-28',
                    'ring-white/60 ring-offset-2 focus:outline-none focus:ring-2',
                    selected ? 'shadow' : 'bg-white',
                  )
                }
                style={{
                  backgroundColor: index === selectedTabIndex ? primaryColor || 'var(--color-primary-600)' : 'white',
                  color: index === selectedTabIndex ? primaryContrastColor : 'black',
                }}
              >
                {category}
              </Tab>
            ))}
          </Tab.List>
        </Tab.Group>
      </div>

      <div key={selectedTabIndex}>
        <Transition
          appear={true}
          show={true}
          enter='transition-opacity easy-linear duration-[300ms]'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          className='flex flex-col gap-4 sm:gap-8 lg:gap-12'
        >
          <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8 sm:gap-12 lg:gap-16 xl:gap-4 justify-items-center w-full'>
            {optionsToShow?.map(({ id, category, ...rest }) => (
              <PlanCard
                key={id}
                className='col-span-1 w-full'
                primaryColor={primaryColor}
                secondaryColor={secondaryColor}
                blurred={planCardOptions?.blurred}
                redirectToTarget={redirectToTarget}
                planBenefitsTitle={planBenefitsTitle}
                customPlanBenefits={customPlanBenefits || planBenefits}
                offerPlan={offerPlan}
                offerDiscount={offerDiscount}
                {...rest}
              />
            ))}
          </div>

          {digitalCertificateOption && (
            <div className='flex flex-col gap-4'>
              <hr className='border-none h-px' style={{ backgroundColor: primaryColor || 'var(--color-primary-600)' }} />

              <div className='flex flex-col gap-6'>
                <div className='text-center md:text-start'>
                  <h4 className='font-semibold text-2xl leading-10'>Serviços adicionais</h4>
                  <p className='font-light text-lg pt-2 leading-tight'>Para facilitar ainda mais a sua vida.</p>
                </div>

                <div className='flex justify-center w-full'>
                  <PlanCard
                    {...digitalCertificateOption}
                    className='col-span-1 w-full md:max-w-sm'
                    primaryColor={primaryColor}
                    secondaryColor={secondaryColor}
                    blurred={planCardOptions?.blurred}
                    redirectToTarget={redirectToTarget}
                    planBenefitsTitle={planBenefitsTitle}
                    customPlanBenefits={digitalCertificateBenefits}
                    offerPlan={offerPlan}
                    offerDiscount={offerDiscount}
                  />
                </div>
              </div>
            </div>
          )}
        </Transition>
      </div>
    </div>
  )
}

export default Plans
